import { Avatar, Box, createStyles, Group, Stack } from '@mantine/core';
import { Icon, Text } from '@repo/foundations';
import { memo } from 'react';
import type { IAiChat } from '../../../api/hooks/aiChats';
import { getResourceIcon } from '../utils';
import { PopoverPreview } from './PopoverPreview';

const ICON_HEIGHT = 20;

const useStyles = createStyles((theme) => ({
	avatar: {
		backgroundColor: theme.other.getColor('fill/primary/default'),
		border: `1px solid ${theme.other.getColor('border/primary/default')}`,
	},
	scrollableList: {
		maxHeight: '480px',
		overflowY: 'auto',
		overflowX: 'hidden',
		'&::-webkit-scrollbar': {
			backgroundColor: 'transparent',
			width: theme.spacing.xs,
		},
		'&::-webkit-scrollbar-track': {
			backgroundColor: 'transparent',
		},
		'&::-webkit-scrollbar-thumb': {
			backgroundColor: 'transparent',
			borderRadius: theme.other.borderRadius.sm,
		},
		'&:hover::-webkit-scrollbar-thumb': {
			backgroundColor: theme.other.getColor('fill/primary/selected'),
		},
		'&::-webkit-scrollbar-thumb:hover': {
			backgroundColor: theme.other.getColor('fill/primary/selected'),
		},
	},
}));

export const QueriedResourcesColumn = memo(
	({ record }: { record: IAiChat }) => {
		const { classes, theme } = useStyles();

		const resources = record.queried_resource_details ?? [];

		if (resources.length === 0) {
			return null;
		}

		const previewIcons = (
			<Group spacing={4} noWrap>
				<Avatar.Group spacing="xs">
					{resources.slice(0, 3).map((resource, i) => {
						const iconInfo = getResourceIcon(resource);
						const uniqueKey = `${resource.type}-${resource.name}-${resource.integration_name || 'no-integration'}-${i}`;
						return (
							<Avatar
								key={uniqueKey}
								radius={`${ICON_HEIGHT}px`}
								miw={`${ICON_HEIGHT}px`}
								mih={`${ICON_HEIGHT}px`}
								h={`${ICON_HEIGHT}px`}
								w={`${ICON_HEIGHT}px`}
								size="sm"
								className={classes.avatar}
							>
								<Icon
									name={iconInfo.name || 'table'}
									color="text/secondary/default"
								/>
							</Avatar>
						);
					})}
				</Avatar.Group>
				<Text size="sm" color="text/primary/default">
					{resources.length === 1 &&
						(resources[0].name.length > 25
							? `${resources[0].name.substring(0, 25)}...`
							: resources[0].name)}
					{resources.length > 1 && `${resources.length} resources`}
				</Text>
			</Group>
		);

		const dropdownContent = (
			<Stack spacing="xs">
				<Text size="xs" color="text/primary/default" weight="semibold" pb="xs">
					Queried resources
				</Text>
				<Box className={classes.scrollableList}>
					<Stack spacing="xs" pr={theme.other.space[3]}>
						{resources.map((resource, i) => {
							const iconInfo = getResourceIcon(resource);
							const uniqueKey = `${resource.type}-${resource.name}-${resource.integration_name || 'no-integration'}-${i}`;
							return (
								<Group key={uniqueKey} spacing="sm" noWrap align="flex-start">
									<Avatar
										radius={`${ICON_HEIGHT}px`}
										miw={`${ICON_HEIGHT}px`}
										mih={`${ICON_HEIGHT}px`}
										h={`${ICON_HEIGHT}px`}
										w={`${ICON_HEIGHT}px`}
										size="sm"
										className={classes.avatar}
									>
										<Icon
											name={iconInfo.name || 'table'}
											color="text/secondary/default"
										/>
									</Avatar>
									<Stack spacing={0}>
										<Text
											truncate
											maw={theme.other.space[60]}
											size="sm"
											color="text/secondary/default"
											weight="semibold"
										>
											{resource.name}
										</Text>
										{resource.integration_name && (
											<Text size="xs" color="text/secondary/default" mt="2xs">
												from {resource.integration_name}
											</Text>
										)}
									</Stack>
								</Group>
							);
						})}
					</Stack>
				</Box>
			</Stack>
		);

		return <PopoverPreview preview={previewIcons} dropdown={dropdownContent} />;
	}
);
